<script >
  // packages
  import { ascending, descending, index } from 'd3-array';
  import { intcomma, pluralize } from 'journalize';

  // components
  import CandidateRow from './CandidateRow.svelte';
  import CandidateRowHeader from './CandidateRowHeader.svelte';

  // constants
  import { DROPPED_OUT_CANDIDATES } from '../constants.js';

  // This svelte file is shared between LocalResults and Results.
  // Results passes in more props than LocalResults
  
  /** @type {string} */
  export let raceId = '';
  
  /** @type {string} */
  export let office = '';
  
  /** @type {string} */
  export let raceType = '';

  /** @type {number} */
  export let totalVotes = 0; 

  /** @type {import('../types.d').CandidateInterface[] | null} */
  export let candidates = []; 

  // --- OPTIONAL PROPS  ---

  /** @type {boolean} */
  export let face = false; 

  /** @type {import('../types.d').Crosswalk | null} */
  export let crosswalk = null;

  /** @type {string} */
  export let caption = '';
  
  /** @type {string} */
  export let captionNote = '';

  /** @type {number | null} */
  export let precinctsReportingPercent = null;

  /** @type {number | null} */
  export let eevp = null;

  /** @type {number} */
  export let numOfwinners = 0;

  /** @type {string} */
  export let componentRaceType = '';

  // --- COMPONENT CONFIGURATION PROPS ---
  export let alwaysShowCandidates = office !== 'P' ? 4 : 2;
  export let expandLabel = 'all';

  export let shouldSortCandidates = false;
  export let hidePrecincts = false;
  export let noVoteNote = '';

  const droppedOutCandidates = DROPPED_OUT_CANDIDATES;

  const numberOfCandidates = candidates.length;

  const tooFewToHide = numberOfCandidates <= alwaysShowCandidates + 1;

  let viewAllCandidates = false;

  const hasParties =
    candidates.some(
      (candidate) => candidate.party && candidate.party.length > 0
    ) &&
    !candidates.every(
      (candidate) => candidate.party && candidate.party === 'np'
    );
  
  const isBallotMeasure = office === 'I' || raceType === 'm' || raceType === 'Measure' ;
  const isPresident = office === 'P';

  $: preppedCandidates = shouldSortCandidates
  ? [...(candidates ?? [])].sort((a, b) => descending(a.votes, b.votes))
  : (candidates ?? [])

  const numDecimalsInPercent = undefined;

</script>

<div class="contest">
  <table class="contest__candidates">
    {#if caption || captionNote}
      <caption class="contest__caption text-left">
          {#if caption}
          <p>{caption}</p>
          {/if}
          {#if captionNote}
            <p class="m-0"><span class="text-xs text-regular color-grey">{captionNote}</span></p>
          {/if}
      </caption>
    {/if}
    <CandidateRowHeader {face} {isBallotMeasure} {isPresident} />
    <tbody>
      {#each preppedCandidates as candidate, idx (candidate.ap_id)}
      <tr
          class="candidate contest__candidate-row contest__candidate-row-{candidate.party}"
          hidden={!(viewAllCandidates || tooFewToHide || idx < alwaysShowCandidates)}>
          <CandidateRow
            familyName={candidate.family_name}
            givenName={candidate.given_name}
            party={candidate.party}
            isWinner={candidate.is_winner}
            isRunoff={candidate.is_runoff}
            isIncumbent={candidate.is_incumbent}
            votes={candidate.votes}
            lookup={face?crosswalk[candidate.ap_id]:''}
            {face}
            {numOfwinners}
            {isBallotMeasure}
            {hasParties}
            {totalVotes}
            {numDecimalsInPercent} 
            hasDroppedOut = {droppedOutCandidates.includes(candidate.ap_id)}
            ap_id = {candidate.ap_id}
            {componentRaceType}
            {raceId}
            />
        </tr>
      {/each}
      {#if noVoteNote}
        <tr class="candidate contest__candidate-row">
          <td colspan={face ? 5 : 4} class="text-sans-serif text-xs text-regular color-grey candidate__cell candidates-note">{noVoteNote}</td>
        </tr>
      {/if}


      
      {#if !tooFewToHide}
        <tr class="candidate contest__candidate-row">
          <td colspan={face?3:2} class="candidate__cell view-all">
            <button on:click={() => (viewAllCandidates = !viewAllCandidates)}>
              View
              {viewAllCandidates ? 'fewer' : expandLabel}
              candidates
              <svg
                class="icon"
                class:icon-angle-down={!viewAllCandidates}
                class:icon-angle-up={viewAllCandidates}>
                <use
                  href="#{viewAllCandidates ? 'icon-angle-up' : 'icon-angle-down'}" />
              </svg>
            </button>
          </td>
          <td
          colspan="2"
          class="contest__precincts text-right">
          {intcomma(totalVotes)}
          total vote{pluralize(totalVotes)}
        </td>
        </tr>
      {:else}
        <tr class="candidate contest__candidate-row">
          <td
          colspan={4}
          class="contest__precincts text-right">
          {intcomma(totalVotes)}
          total vote{pluralize(totalVotes)}
        </td>
        </tr>
      {/if}
    </tbody>
    <tfoot>
      <tr>
        <td colspan={face ? 5 : 4} class="contest__precincts text-right">
          {#if !hidePrecincts}
            {#if eevp !== null}
              {eevp.toFixed(1)}% of expected vote in
            {:else if (precinctsReportingPercent !== null)}
              {precinctsReportingPercent}% precincts reporting
            {/if}
          {/if}
        </td>
      </tr>
    </tfoot>
  </table>
</div>
